"use client";
import { JSX, useState } from "react";
import { ChevronDown, Languages } from "lucide-react";
import { Facebook, Instagram } from "react-bootstrap-icons";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import SafeLink from "@/components/SafeLink";
import { Locale } from "@/models/Locale";

export function Footer(): JSX.Element {
  const { locale } = useParams<{ locale: Locale }>();
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("components.footer");

  const toggleDropdown = () => setIsOpen(!isOpen);
  function getLanguageName(localeSuffix: string) {
    switch (localeSuffix) {
      case "tr":
        return "Türkçe";
      case "ar":
        return "العربية";
      case "ru":
        return "Русский";
      case "de":
        return "Deutsch";
      case "fr":
        return "Français";
      case "es":
        return "Español";
      case "zh":
        return "中文";
      case "en":
      default:
        return "English";
    }
  }
  return (
    <footer className="flex flex-col-reverse bg-white lg:h-100 lg:flex-row lg:items-center lg:justify-between">
      {/* Left side */}
      <div className="flex w-full flex-row px-20 py-20 lg:h-full lg:ps-30 lg:pe-0">
        <div className="flex flex-row flex-wrap justify-center gap-8">
          {/* Company */}
          <div className="flex flex-col justify-between lg:items-start">
            <div className="flex flex-col items-center gap-6 sm:items-start">
              <SafeLink href="/" locale={locale} className="h-[60px] w-[260px]">
                <Image
                  src="/dashboard-assets/plus_menu_brand.png"
                  alt="plus menu brand"
                  width={180}
                  height={51}
                />
              </SafeLink>
              {/* Social Media Icons*/}
              <div className="ms-2 flex flex-row gap-2">
                <SafeLink href="https://www.instagram.com/plusmenu.co/">
                  <Instagram size={18} />
                </SafeLink>
                <SafeLink href="https://www.facebook.com/people/Plus-Menu/61577633013389/">
                  <Facebook size={18} />
                </SafeLink>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-6 sm:mt-16">
              {/*Language Switch Dropdown*/}
              <div className="relative ms-2 inline-block text-left">
                <button
                  onClick={toggleDropdown}
                  className="border-ht-brown text-ht-brown flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 font-medium shadow-sm hover:bg-gray-50"
                >
                  <Languages className="h-5 w-5" />
                  {getLanguageName(locale)}
                  <ChevronDown className="h-5 w-5" />
                </button>

                {isOpen && (
                  <div className="absolute bottom-full z-10 mb-2 min-w-44 origin-top-right rounded-md bg-white ring-1 shadow-sm ring-black focus:outline-none">
                    <div
                      className="py-1"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="options-menu-button"
                    >
                      {[
                        { name: "English", suffix: "en" },
                        { name: "Türkçe", suffix: "tr" },
                        { name: "العربية", suffix: "ar" },
                        { name: "Русский", suffix: "ru" },
                        { name: "Deutsch", suffix: "de" },
                        { name: "Français", suffix: "fr" },
                        { name: "Español", suffix: "es" },
                        { name: "中文", suffix: "zh" },
                      ].map((languageObject, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            router.push(`/${languageObject.suffix}`);
                          }}
                          className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {languageObject.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <span className="hidden text-sm font-medium sm:block">
                {t("all_rights_reserved")}
              </span>
            </div>
          </div>

          {/* Links Wrapper */}
          <div className="flex flex-row gap-8">
            {/* Links */}
            <div className="flex flex-col justify-start gap-2">
              <h4 className="font-bold">{t("links")}</h4>
              <div id="divider" className="w-20 border-t border-gray-700"></div>
              <ul>
                <li>
                  <SafeLink href="/menu/1" className="">
                    {t("demo_menu")}
                  </SafeLink>
                </li>
                <li>
                  <SafeLink href="/#price">{t("prices")}</SafeLink>
                </li>
                <li>
                  <SafeLink href="/sign-in">{t("login")}</SafeLink>
                </li>
                <li>
                  <SafeLink href="/sign-up">{t("sign_up")}</SafeLink>
                </li>
              </ul>
            </div>
            {/* Company */}
            <div className="flex flex-col justify-start gap-2">
              <h4 className="font-bold">{t("company")}</h4>
              <div id="divider" className="w-20 border-t border-gray-700"></div>
              <ul>
                {/* <li>
                  <a href="#" className="">
                    {t("about_us")}
                  </a>
                </li>
                <li>
                  <a href="#" className="">
                    {t("privacy")}
                  </a>
                </li>
                <li>
                  <a href="#" className="">
                    KVKK
                  </a>
                </li> */}
              </ul>
            </div>
            {/* Resorces */}
            {/* <div className="flex flex-col justify-start gap-2">
              <h4 className="font-bold">{t("resources")}</h4>
              <div id="divider" className="w-20 border-t border-gray-700"></div>
              <ul>
                <li>
                  <a href="#" className="">
                    {t("blog")}
                  </a>
                </li>
                <li>
                  <a href="#price" className="">
                    {t("prices")}
                  </a>
                </li>
              </ul>
            </div> */}
          </div>
        </div>
      </div>
      {/* Right side */}
      <div className="flex flex-col items-center justify-start gap-8 bg-green-50 py-16 pt-20 lg:h-full lg:items-end lg:gap-0 lg:bg-transparent lg:pe-30 lg:pb-0">
        <span className="text-ht-purple text-center text-2xl font-bold whitespace-nowrap lg:text-end rtl:w-full rtl:lg:text-center">
          {t("your_menu")}
          <br />
          {t("their_language")}
        </span>
        <div className="relative flex w-fit grow flex-col items-end justify-center bg-radial from-[#8cd48d] from-0% to-70% px-12">
          <Image
            src="/dashboard-assets/home/callback-guy.png"
            alt=""
            width={600}
            height={600}
            className="absolute -start-40 top-16 hidden h-auto w-50 lg:block rtl:scale-x-[-1] rtl:transform"
          />
        </div>
      </div>
    </footer>
  );
}
